import { useEffect, useRef } from "react";

const DISMISS_THRESHOLD_PX = 90;
const VERTICAL_DOMINANCE_RATIO = 1.25;
const DISMISS_ACTIVATION_PX = 12;

/** Touch drag-down to exit CSS fullscreen (mobile lightbox pattern). */
export function useGallerySwipeToDismiss({
  enabled,
  getTargetElement,
  onDismiss,
}) {
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!enabled) return undefined;

    let element = null;
    let content = null;
    let lastDeltaY = 0;
    let dragState = null;

    const resetStyles = () => {
      if (!content) return;
      content.style.transition = "";
      content.style.transform = "";
      content.style.opacity = "";
    };

    const onTouchStart = (event) => {
      if (event.touches.length !== 1) return;
      dragState = {
        startX: event.touches[0].clientX,
        startY: event.touches[0].clientY,
        trackingDismiss: false,
      };
      lastDeltaY = 0;
    };

    const onTouchMove = (event) => {
      if (!dragState || event.touches.length !== 1) return;

      const deltaX = event.touches[0].clientX - dragState.startX;
      const deltaY = event.touches[0].clientY - dragState.startY;
      lastDeltaY = deltaY;

      if (
        !dragState.trackingDismiss &&
        deltaY > DISMISS_ACTIVATION_PX &&
        Math.abs(deltaY) > Math.abs(deltaX) * VERTICAL_DOMINANCE_RATIO
      ) {
        dragState.trackingDismiss = true;
      }

      if (!dragState.trackingDismiss || !content) return;

      if (deltaY > 0) {
        event.preventDefault();
        const progress = Math.min(deltaY / 280, 1);
        content.style.transition = "none";
        content.style.transform = `translateY(${deltaY}px)`;
        content.style.opacity = String(1 - progress * 0.45);
      }
    };

    const onTouchEnd = () => {
      if (!dragState || !content) {
        dragState = null;
        return;
      }

      content.style.transition = "transform 0.22s ease, opacity 0.22s ease";

      if (dragState.trackingDismiss && lastDeltaY >= DISMISS_THRESHOLD_PX) {
        onDismissRef.current?.();
        resetStyles();
      } else {
        content.style.transform = "";
        content.style.opacity = "";
      }

      dragState = null;
      lastDeltaY = 0;
    };

    const attach = () => {
      element = getTargetElement();
      if (!element) return false;

      content =
        element.querySelector(".image-gallery-content") || element;

      element.addEventListener("touchstart", onTouchStart, { passive: true });
      element.addEventListener("touchmove", onTouchMove, { passive: false });
      element.addEventListener("touchend", onTouchEnd);
      element.addEventListener("touchcancel", onTouchEnd);
      return true;
    };

    const frameId = requestAnimationFrame(() => {
      if (!attach()) requestAnimationFrame(attach);
    });

    return () => {
      cancelAnimationFrame(frameId);
      if (element) {
        element.removeEventListener("touchstart", onTouchStart);
        element.removeEventListener("touchmove", onTouchMove);
        element.removeEventListener("touchend", onTouchEnd);
        element.removeEventListener("touchcancel", onTouchEnd);
      }
      resetStyles();
    };
  }, [enabled, getTargetElement]);
}
